import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewIcon from '@mui/icons-material/Article';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import RowIcon from '@mui/icons-material/TableRows';
import { useConfirm } from 'material-ui-confirm';
import { formatDate, formatDistance } from 'date-fns';
import usePolling from '@eballoi/react-use-polling';
import { useTranslation } from 'react-i18next';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import TextField from '@/components/textField';
import DataGridTable from '@/components/datagridTable/datagridTable';
import SelectField from '../selectField';
import RightDrawer from '../drawerPanel';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useUserContext } from '@/contexts/userContext';
import DataSourceItemUpload from './dataSourceItemUpload';
import { useElementSize } from '@custom-react-hooks/use-element-size';

type Props = { dataSourceInstance: any; maxHeight: number; onOpenDataSourceItem: (item: any) => void };

const DATASOURCE_ITEM_STATUSES = ['SCANNED', 'PROCESSED', 'FAILED'];

const CollapsibleRow = ({ group, canDelete, onViewFile, onViewRow, onDeleteFile }: any) => {
  const [open, setOpen] = useState(false);
  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1;

  const statusText = group.hasError
    ? 'Error'
    : group.processedCount === group.totalCount
      ? 'Processed'
      : `Scanned (${group.processedCount}/${group.totalCount})`;

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell width="50px" sx={{ pr: 0 }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FileIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600}>
              {group.filename}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          <Chip
            label={statusText}
            size="small"
            color={group.hasError ? 'error' : group.processedCount === group.totalCount ? 'success' : 'warning'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
          />
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>File Document</TableCell>
        <TableCell sx={{ py: 1.5 }}>{formatBytes(group.itemSize)}</TableCell>
        <TableCell sx={{ py: 1.5 }}>
          {group.lastScannedDate ? formatDate(group.lastScannedDate, dataTimeFormat) : ''}
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          {group.lastModifiedDate ? formatDate(group.lastModifiedDate, dataTimeFormat) : ''}
        </TableCell>
        <TableCell align="right" sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="View File">
              <IconButton size="small" onClick={onViewFile}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Delete File">
                <IconButton size="small" color="error" onClick={onDeleteFile}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, pl: 4, borderLeft: '2px solid', borderColor: 'divider', pb: 1 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                gutterBottom
                component="div"
                sx={{ mb: 1 }}
              >
                Spreadsheet Rows ({group.rows.length})
              </Typography>
              <Table size="small" aria-label="rows">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" fontWeight={700}>
                        Row Name
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight={700}>
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" fontWeight={700}>
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.rows.map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ py: 0.75 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <RowIcon fontSize="inherit" sx={{ opacity: 0.6 }} />
                          <Typography variant="body2">{row.rowDisplayName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="caption" color={row.error ? 'error.main' : 'text.primary'}>
                          {row.error ? 'Error' : row.lastScannedDate ? 'Processed' : 'Not Processed'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75 }}>
                        <Tooltip title="View Row Details">
                          <IconButton size="small" onClick={() => onViewRow(row)}>
                            <ViewIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  if (!bytes) return '--';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const DataSourceItems = ({ dataSourceInstance = {}, maxHeight, onOpenDataSourceItem }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const confirm = useConfirm();
  const { hasPermission, user } = useUserContext();

  const [headerRef, headerSize] = useElementSize();

  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState('');

  const [showFiters, setShowFiters] = useState(false);

  const [dataSourceItems, setDataSourceItems] = useState<any[]>([]);
  const [currentDataSourceItem, setCurrentDataSourceItem] = useState<any>();
  const [showDataSourceItemUploadPanel, setShowDataSourceItemUploadPanel] = useState(false);
  const [error, setError] = useState<any>();

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1;

  const viewDataSourceItems = hasPermission(`pipeline:dataSources:items:view`, dataSourceInstance?.userAccess);
  const canUploadDataSourceItem = hasPermission(`pipeline:dataSources:items:upload`, dataSourceInstance?.userAccess);
  const canDeleteDataSourceItem = hasPermission(`pipeline:dataSources:items:delete`, dataSourceInstance?.userAccess);

  const maxDocumentsLimit = useMemo(() => {
    const permissions = user?.licenseData?.permissions || [];
    const permission = permissions.find((p: any) => p.fqn === 'pipeline:dataSources:documents:upload');
    const allowed = permission?.attributes?.maxDocumentsPerDataSource;
    return typeof allowed === 'number' ? allowed : 10;
  }, [user?.licenseData]);

  const { data: runStatus } = usePolling<any>(
    async () => {
      if (dataSourceInstance.id) {
        return apiContext.getDataSourceInstanceProcessingStatus(
          dataSourceInstance.dataSourceDefId,
          dataSourceInstance.id,
        );
      } else {
        return;
      }
    },
    { interval: 60000 },
  );

  const [runStatusMessage, setRunStatusMessage] = useState('');

  useEffect(() => {
    if (runStatus?.runNumber)
      setRunStatusMessage(
        t(runStatus.runEndTime ? 'dataSource.runStatus' : 'dataSource.runStatusInProgress', {
          runNumber: runStatus.runNumber,
          runStartTime: formatDate(runStatus.runStartTime, dataTimeFormat),
          mins: formatDistance(runStatus.runEndTime || new Date(), runStatus.runStartTime),
          count: runStatus.noOfItems,
        }),
      );
  }, [runStatus]);

  const getDataSourceItems = async () => {
    setError(null);
    try {
      setLoading(true);

      const dataSourceItems = await apiContext.getDataSourceInstanceItems(
        dataSourceInstance.dataSourceDefId,
        dataSourceInstance.id,
      );
      setDataSourceItems(dataSourceItems);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dataSourceInstance?.id) {
      getDataSourceItems();
    }
  }, [dataSourceInstance?.id, JSON.stringify(runStatus)]);

  const columns: GridColDef[] = useMemo(() => {
    const showModifiedDate = dataSourceItems.some((item) => item.lastModifiedDate);
    const cols: GridColDef[] = [
      { field: 'itemName', headerName: 'Name', minWidth: 100, flex: 1 },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        valueGetter: (value: any, row: any) => {
          return row.error ? 'Error' : row.lastScannedDate ? 'Processed' : 'Not Processed';
        },
      },
      {
        field: 'itemType',
        headerName: 'Type',
        width: 110,
        valueGetter: (value: any, row: any) => value || 'Document',
      },
      {
        field: 'itemSize',
        headerName: 'Size',
        width: 100,
        valueGetter: (value: any, row: any) => formatBytes(value),
      },
      {
        field: 'lastScannedDate',
        headerName: 'Last scanned time',
        width: 180,
        valueGetter: (value: any, row: any) => {
          return value ? formatDate(value, dataTimeFormat) : '';
        },
      },
    ];
    if (showModifiedDate) {
      cols.push({
        field: 'lastModifiedDate',
        headerName: 'Last modified time',
        width: 180,
        valueGetter: (value: any, row: any) => {
          return value ? formatDate(value, dataTimeFormat) : '';
        },
      });
    }

    cols.push({
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params: any) => {
        const actions = [];
        if (viewDataSourceItems) {
          actions.push(
            <GridActionsCellItem icon={<ViewIcon />} label="View" onClick={() => onOpenDataSourceItem(params.row)} />,
          );
        }
        if (dataSourceInstance.dataSourceDefId === 'userUpload' && canDeleteDataSourceItem) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => deleteDataSourceItem(params.row)}
            />,
          );
        }
        return actions;
      },
    });

    return cols;
  }, [
    dataSourceInstance.dataSourceDefId,
    dataSourceItems,
    viewDataSourceItems,
    canDeleteDataSourceItem,
    dataTimeFormat,
  ]);

  const hasParentChildItems = useMemo(() => {
    return dataSourceInstance?.dataSourceDef?.metadata?.hasParentChildItems === true;
  }, [dataSourceInstance?.dataSourceDef?.metadata]);

  const groupedFiles = useMemo(() => {
    if (!hasParentChildItems) return [];

    const parents = dataSourceItems.filter((i: any) => !i.parentItemName);
    const children = dataSourceItems.filter((i: any) => !!i.parentItemName);

    return parents.map((parent: any) => {
      const rows = children
        .filter((child: any) => child.parentItemName === parent.itemName)
        .map((child: any) => ({
          ...child,
          rowDisplayName: child.itemName.replace(parent.itemName + '/', '').replace(/\//g, ' / '),
        }));

      return {
        id: parent.itemName,
        filename: parent.itemName,
        itemSize: parent.itemSize || 0,
        lastModifiedDate: parent.lastModifiedDate,
        lastScannedDate: parent.lastScannedDate,
        hasError: rows.some((r: any) => r.error),
        processedCount: rows.filter((r: any) => r.lastScannedDate).length,
        totalCount: rows.length,
        rows,
        rawItem: parent,
      };
    });
  }, [dataSourceItems, hasParentChildItems]);

  const isUnderLimit = useMemo(() => {
    const count = hasParentChildItems ? groupedFiles.length : dataSourceItems.length;
    return maxDocumentsLimit === -1 || count < maxDocumentsLimit;
  }, [hasParentChildItems, groupedFiles.length, dataSourceItems.length, maxDocumentsLimit]);

  const searchDataSourceItems = async () => {
    if (!fileName && !status) {
      return;
    }
    setError(null);
    try {
      setLoading(true);

      const dataSourceItems = await apiContext.searchDataSourceInstanceItems(
        dataSourceInstance.dataSourceDefId,
        dataSourceInstance.id,
        status ? { fileName, statuses: [status] } : { fileName },
      );

      setDataSourceItems(dataSourceItems);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const refreshDataSourceItems = () => {
    if (fileName || status) {
      searchDataSourceItems();
    } else {
      getDataSourceItems();
    }
  };

  const runPipeline = (reingest: boolean) => {
    confirm({
      description: reingest
        ? 'This action will delete existing items  and re-ingest all items from the data source. Do you want to continue?'
        : 'This action will execute the ingestion pipeline to look for new and updated files.  Do you want to continue?',
    }).then(async () => {
      setError(null);
      try {
        setLoading(true);
        await apiContext.runDataSourceIngestionPipeline(
          dataSourceInstance.dataSourceDefId,
          dataSourceInstance.id,
          reingest,
        );
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    });
  };

  const deleteDataSourceItem = (file: any) => {
    confirm({
      description: `Do you want to delete file "${file.filename || file.itemName}"? This will delete the file and all its records.`,
    }).then(async () => {
      setError(null);
      try {
        setLoading(true);
        const filenameToDelete = file.filename || file.itemName;
        await apiContext.deleteDataSourceInstanceItem(
          dataSourceInstance.dataSourceDefId,
          dataSourceInstance.id,
          filenameToDelete,
        );
        // Remove the parent item and all its children from local state
        const newDataSourceItems = dataSourceItems.filter(
          (f: any) => f.itemName !== filenameToDelete && f.parentItemName !== filenameToDelete,
        );
        setDataSourceItems(newDataSourceItems);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    });
  };

  const toggleFilters = () => {
    setShowFiters(!showFiters);
  };

  const clearFilters = async () => {
    setFileName('');
    setStatus('');
    setError(null);
    try {
      setLoading(true);
      const dataSourceItems = await apiContext.getDataSourceInstanceItems(
        dataSourceInstance.dataSourceDefId,
        dataSourceInstance.id,
      );
      setDataSourceItems(dataSourceItems);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const openDataSourceItemUploadPanel = () => {
    setShowDataSourceItemUploadPanel(true);
  };

  const hideDataSourceItemUploadPanel = async () => {
    setShowDataSourceItemUploadPanel(false);
  };

  return (
    <Stack justifyContent="flex-start" spacing={2}>
      {runStatusMessage && <Alert severity="info">{runStatusMessage}</Alert>}
      {error && <ErrorMessage error={error} />}
      <Box sx={{ overflowY: 'auto', p: 1, height: `${maxHeight}px` }}>
        <Grid container spacing={2} padding={1} ref={headerRef}>
          <Grid container justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1} size={12}>
            {!showFiters && (
              <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center">
                <Button size="small" onClick={toggleFilters} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  Show filters
                </Button>
              </Grid>
            )}
            <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
            <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
            <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center">
              <Button
                size="small"
                variant="contained"
                onClick={() => runPipeline(false)}
                sx={{ borderRadius: '20px', textTransform: 'none', px: 2, width: { xs: '100%', sm: 'auto' } }}
              >
                Sync Updates
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => runPipeline(true)}
                sx={{ borderRadius: '20px', textTransform: 'none', px: 2, width: { xs: '100%', sm: 'auto' } }}
              >
                Force Re-sync
              </Button>
            </Grid>
            {(dataSourceInstance.dataSourceDefId === 'userUpload' || hasParentChildItems) &&
              canUploadDataSourceItem &&
              isUnderLimit && (
                <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openDataSourceItemUploadPanel}
                    sx={{ borderRadius: '20px', textTransform: 'none', px: 2, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Upload Items
                  </Button>
                </Grid>
              )}
            <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={refreshDataSourceItems}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      borderColor: 'secondary.main',
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          {showFiters && (
            <Grid container justifyContent="flex-start" spacing={1} size={12}>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <TextField label="File name" value={fileName} required maxLength={100} onChange={setFileName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 'grow' }}>
                <SelectField
                  options={DATASOURCE_ITEM_STATUSES}
                  label="Status"
                  value={status}
                  autoFocus
                  onChange={setStatus}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 'auto' }} alignSelf="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={!fileName && !status}
                  onClick={searchDataSourceItems}
                  sx={{ mt: { xs: 1, sm: 2 }, width: '100%' }}
                >
                  Search
                </Button>
              </Grid>
              <Grid size={{ xs: 6, sm: 'auto' }} alignSelf="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!fileName && !status}
                  onClick={clearFilters}
                  sx={{ mt: { xs: 1, sm: 2 }, width: '100%' }}
                >
                  Clear
                </Button>
              </Grid>
              <Grid size="grow" sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>
              <Grid size={{ xs: 12, sm: 'auto' }} alignSelf="center" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Button size="small" onClick={toggleFilters} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  Hide filters
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        {hasParentChildItems ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: `${maxHeight - headerSize.height - 20}px`, overflowY: 'auto' }}
          >
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      File Name
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Type
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Size
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Last Scanned Time
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Last Modified Time
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight={700}>
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedFiles.map((group) => {
                  const handleViewFile = () => {
                    onOpenDataSourceItem(group.rawItem);
                  };

                  return (
                    <CollapsibleRow
                      key={group.id}
                      group={group}
                      canDelete={canDeleteDataSourceItem}
                      onViewFile={handleViewFile}
                      onViewRow={onOpenDataSourceItem}
                      onDeleteFile={() => deleteDataSourceItem({ filename: group.filename })}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <DataGridTable
            rows={dataSourceItems}
            columns={columns}
            loading={loading}
            height={`${maxHeight - headerSize.height - 20}px`}
          />
        )}
      </Box>
      {showDataSourceItemUploadPanel && (
        <RightDrawer open={showDataSourceItemUploadPanel} onClose={hideDataSourceItemUploadPanel} minWidth={600}>
          <DataSourceItemUpload dataSourceInstance={dataSourceInstance} onCancel={hideDataSourceItemUploadPanel} />
        </RightDrawer>
      )}
    </Stack>
  );
};

export default DataSourceItems;
